/*
 * The delimiters each locale's typography uses, by language with a regional variant where
 * the region differs: the quotation marks, the dash, which marks the locale sets off
 * before them and with what, and whether a question or exclamation opens with an
 * inverted mark. Derived from the locale alone. French sets `?`, `!`, `;` and `:` off with
 * a narrow no-break space in France, and in Québec writes `:` with a no-break space and the
 * other three flush. A served region's rendered text takes its own row's spacing whatever
 * the stored message wrote (`withServedSpacing`), so the base language's row, which both
 * regions read, accepts either convention and only a regional row is held to one.
 */

export const NARROW_NO_BREAK_SPACE = ' '
export const NO_BREAK_SPACE = ' '
/** Flush against the word: nothing before the mark. */
export const FLUSH = ''

export interface Typography {
    /** The opening and closing quotation marks. */
    readonly quotes: readonly [string, string]
    /** The dash between clauses, with the spacing it takes. */
    readonly dash: string
    /** The marks the locale sets off before them, each with the characters accepted
     *  before it: a space, or flush against the word. A mark absent here takes no space
     *  in this locale. */
    readonly spaceBefore: Readonly<Record<string, readonly string[]>>
    /** A question or exclamation opens with `¿` or `¡`. */
    readonly invertedMarks: boolean
}

const FRANCE: Typography['spaceBefore'] = {
    '?': [NARROW_NO_BREAK_SPACE],
    '!': [NARROW_NO_BREAK_SPACE],
    ';': [NARROW_NO_BREAK_SPACE],
    ':': [NARROW_NO_BREAK_SPACE],
}

const BY_LANGUAGE: Readonly<Record<string, Typography>> = {
    en: { quotes: ['“', '”'], dash: '—', spaceBefore: {}, invertedMarks: false },
    de: { quotes: ['„', '“'], dash: ' – ', spaceBefore: {}, invertedMarks: false },
    es: { quotes: ['«', '»'], dash: ' – ', spaceBefore: {}, invertedMarks: true },
    fr: {
        quotes: ['«', '»'],
        dash: ' – ',
        spaceBefore: {
            '?': [NARROW_NO_BREAK_SPACE, FLUSH],
            '!': [NARROW_NO_BREAK_SPACE, FLUSH],
            ';': [NARROW_NO_BREAK_SPACE, FLUSH],
            ':': [NARROW_NO_BREAK_SPACE, NO_BREAK_SPACE],
        },
        invertedMarks: false,
    },
    it: { quotes: ['«', '»'], dash: ' – ', spaceBefore: {}, invertedMarks: false },
    nl: { quotes: ['“', '”'], dash: ' – ', spaceBefore: {}, invertedMarks: false },
}

const BY_LOCALE: Readonly<Record<string, Partial<Typography>>> = {
    'es-mx': { quotes: ['“', '”'] },
    'fr-fr': { spaceBefore: FRANCE },
    'fr-ca': { spaceBefore: { ':': [NO_BREAK_SPACE] } },
}

/** A locale's typography, or undefined for a language the table does not know. */
export function typographyOf(locale: string): Typography | undefined {
    const language = BY_LANGUAGE[locale.split('-')[0]]
    return language === undefined ? undefined : { ...language, ...BY_LOCALE[locale] }
}

const spaceName = (space: string) =>
    space === NARROW_NO_BREAK_SPACE
        ? 'a narrow no-break space (U+202F)'
        : space === NO_BREAK_SPACE
          ? 'a no-break space (U+00A0)'
          : 'nothing'

/** `a narrow no-break space (U+202F) or nothing`. */
export const acceptedName = (accepted: readonly string[]) => accepted.map(spaceName).join(' or ')

export const dashName = (dash: string) =>
    dash === '—' ? 'an unspaced em dash (—)' : `a spaced en dash (${dash})`

const MARKS = new Set([',', '.', ';', ':', '?', '!'])
export const SPACE = /[\s\u00a0\u202f]/u
const DIGIT = /\p{Nd}/u

/** Whether a mark takes its locale's spacing where it stands: not at the start of a text,
 *  not after another mark (`?!`), and a colon not between digits (`10:30`). */
export function isSetOffPosition(
    char: string,
    before: string | undefined,
    after: string | undefined,
): boolean {
    if (before === undefined || MARKS.has(before)) {
        return false
    }
    return !(char === ':' && DIGIT.test(before) && after !== undefined && DIGIT.test(after))
}

/**
 * A text in its locale's punctuation spacing: each mark the locale's language sets off
 * takes the locale's own space before it, or none where the locale writes it flush. A
 * language that sets no mark off is returned as written. The text is the copy's own, with
 * any value still standing behind a placeholder, so a value is never respaced. `preceding`
 * is the character a reader sees just before the text, which places a mark opening it; a
 * space there belongs to the text before and is left as written.
 */
export function withServedSpacing(text: string, locale: string, preceding?: string): string {
    const marks = typographyOf(locale.split('-')[0])?.spaceBefore ?? {}
    if (Object.keys(marks).length === 0) {
        return text
    }
    const own = typographyOf(locale)!.spaceBefore
    const chars = [...text]
    const out: string[] = []
    chars.forEach((char, index) => {
        const before = index === 0 ? preceding : chars[index - 1]
        if (
            char in marks &&
            before !== undefined &&
            isSetOffPosition(char, before, chars[index + 1]) &&
            !(index === 0 && SPACE.test(before))
        ) {
            if (SPACE.test(before)) {
                out.pop()
            }
            out.push(own[char]?.find((space) => space !== FLUSH) ?? FLUSH)
        }
        out.push(char)
    })
    return out.join('')
}
